import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import useMotionPreference from "@/hooks/useMotionPreference";

const ITEMS = [
  "BCA at Christ University · graduating June 2027",
  "Building full-stack web applications",
  "Exploring offline and real-time workflows",
];

function CurrentItem({ label, index, progress, reduceMotion }) {
  const start = 0.18 + index * 0.12;
  const end = start + 0.18;
  const opacity = useTransform(progress, [start, end], [0, 1]);
  const x = useTransform(progress, [start, end], [26, 0]);

  return (
    <motion.span
      style={reduceMotion ? undefined : { opacity, x }}
      className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3.5 py-1.5 font-mono text-xs text-white/60"
    >
      {label}
    </motion.span>
  );
}

export default function Currently() {
  const ref = useRef(null);
  const reduceMotion = useMotionPreference();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const opacity = useTransform(scrollYProgress, [0.06, 0.23, 0.82, 1], [0, 1, 1, 0.75]);
  const y = useTransform(scrollYProgress, [0.06, 0.23, 0.82, 1], [28, 0, 0, -12]);
  const lineScale = useTransform(scrollYProgress, [0.18, 0.68], [0, 1]);
  const ringScale = useTransform(scrollYProgress, [0.18, 0.42, 0.7], [0.7, 2.5, 1]);
  const ringOpacity = useTransform(scrollYProgress, [0.18, 0.42, 0.7], [0, 0.7, 0]);

  return (
    <div ref={ref} data-testid="currently" className="mx-auto w-full max-w-7xl px-6 py-8 md:px-10">
      <motion.div
        style={reduceMotion ? undefined : { opacity, y }}
        className="relative overflow-hidden rounded-2xl border border-white/5 bg-[#0a0a0a] px-6 py-5"
      >
        <motion.span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-px origin-left bg-brand-500/40"
          style={reduceMotion ? { scaleX: 1 } : { scaleX: lineScale }}
        />
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex shrink-0 items-center gap-2">
            <span className="relative flex h-2 w-2">
              <motion.span
                aria-hidden="true"
                className="absolute inset-0 rounded-full bg-brand-400"
                style={reduceMotion ? { opacity: 0 } : { scale: ringScale, opacity: ringOpacity }}
              />
              <span className="relative h-2 w-2 rounded-full bg-brand-500" />
            </span>
            <span className="font-mono text-[11px] uppercase tracking-widest text-white/40">Currently</span>
          </div>
          <div className="hidden h-4 w-px bg-white/10 sm:block" />
          <div className="flex flex-wrap gap-2">
            {ITEMS.map((label, index) => (
              <CurrentItem key={label} label={label} index={index} progress={scrollYProgress} reduceMotion={reduceMotion} />
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
